import { useState } from 'react';
import { Square } from 'lucide-react';
import clsx from 'clsx';

interface StopButtonProps {
  /**
   * Wired to `useStreaming().abort()` (Session 1 dual-fire — local SSE cancel
   * + server-side `POST /messages/stream/abort`). The optimistic status flip
   * to `'aborted'` happens inside the hook so directive §10.5's 500ms budget
   * is met regardless of network round-trip.
   */
  onAbort: () => Promise<void>;
}

/**
 * Inline Stop button (directive §8.4, Phase 2 Session 4). Mounted next to
 * `<ChatInput>` in `routes/builder.tsx` while `status === 'streaming'`.
 * Single instance — global stop, not per-tool.
 */
export function StopButton({ onAbort }: StopButtonProps) {
  const [pending, setPending] = useState(false);

  const handleClick = async () => {
    setPending(true);
    try {
      await onAbort();
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={pending}
      aria-label="Stop streaming"
      data-testid="stop-button"
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[2px] border',
        'border-danger/20 bg-danger-dim text-danger',
        'hover:border-danger/40 disabled:opacity-50 disabled:cursor-not-allowed',
        'text-xs font-mono cursor-pointer',
      )}
    >
      <Square className="w-3 h-3" fill="currentColor" />
      <span>Stop</span>
    </button>
  );
}
